import Dropdown from "../../../node_modules/bootstrap/js/src/dropdown";
import { getNextSibling } from "../utils/dom";

const ESCAPE_KEY = "Escape";
const SPACE_KEY = "Space";
const TAB_KEY = "Tab";
const ARROW_UP_KEY = "ArrowUp";
const ARROW_DOWN_KEY = "ArrowDown";
const BACKSPACE_KEY = "Backspace";
const ENTER_KEY = "Enter";
const DELETE_KEY = "Delete";
const CLASS_NAME_SHOW = "show";
const ITEM_CLASSNAME = "dropdown-item";
const DROPDOWN_CLASSNAME = "dropdown-menu";

export default class Autocomplete {

    static uniqueItemIndex = 0;

    get dropdownElement() {
        return getNextSibling(this.element, `.${DROPDOWN_CLASSNAME}`);
    }

    get dropdown() {
        return Dropdown.getOrCreateInstance(this.element);
    }

    get defaultOptions() {
        return {
            valueTarget: null,
            valueKey: "value",
            textKey: "text",
            minLength: 1,
            query: null,
            itemTemplate: function (item) {
                return `<li class="${ITEM_CLASSNAME}" role="option" data-value="${(item[this.valueKey] || item[this.valueKey])}">${item[this.textKey]}</a></li>`
            }
        };
    }

    get isActive() {
        return this.dropdownElement.classList.contains(CLASS_NAME_SHOW);
    }

    get allItems() {
        return this.dropdownElement.querySelectorAll(`.${ITEM_CLASSNAME}`);
    }

    get currentItem() {
        return this.dropdownElement.querySelector(`.${ITEM_CLASSNAME}[aria-selected="true"]`);
    }

    set currentItem(item) {
        this.allItems.forEach(i => {
            i.removeAttribute("aria-selected");
            i.classList.remove("active");
        });

        item.setAttribute("aria-selected", "true");
        item.classList.add("active");
    }

    get lastItem() {
        return this.dropdownElement.querySelector(`.${ITEM_CLASSNAME}:last-of-type`);
    }

    get firstItem() {
        return this.dropdownElement.querySelector(`.${ITEM_CLASSNAME}`);
    }

    get hasItems() {
        return this.allItems != null && this.allItems.length > 0;
    }

    constructor(elementOrSelector, options) {
        this.element = typeof elementOrSelector === "string" ? document.querySelector(elementOrSelector) : elementOrSelector;
        this.options = this.defaultOptions;

        for (var prop in options) {
            if (options.hasOwnProperty(prop)) {
                this.options[prop] = options[prop];
            }
        }

        this.addAttributes();
        this.bindEvents();
    }

    destroy() {

        this.element.removeEventListener("keyup", this.handleKeyUp);

        this.element.removeEventListener("keydown", this.handleKeyDown);

        this.dropdownElement.removeEventListener("click", this.handleItemClick);

        this.element = null;
        this.dropdown = null;
        this.options = null;
    }

    addAttributes() {
        this.element.setAttribute("role", "combobox");
        this.element.setAttribute("aria-autocomplete", "list");
        this.element.setAttribute("aria-expanded", "false");
        this.dropdownElement.setAttribute("role", "listbox");

        if (this.dropdownElement.hasAttribute("id")) {
            this.element.setAttribute("aria-controls", this.dropdownElement.id)
        }
        else {
            this.dropdownElement.id = this.getUniqueId();
            this.element.setAttribute("aria-controls", this.dropdownElement.id)
        }
    }

    bindEvents() {
        this.element.addEventListener("show.bs.dropdown", e => {

            // Dont"t show the dropdown unless there are enough characters
            if (this.element.value.length < this.options.minLength) {
                e.stopPropagation();
                e.preventDefault();
            }

        });

        this.element.addEventListener("keyup", this.handleKeyUp);

        this.element.addEventListener("keydown", this.handleKeyDown);

        this.dropdownElement.addEventListener("click", this.handleItemClick, false);
    }

    handleKeyUp = e => {

        if (e.target.value.length < this.options.minLength) {
            this.hideDropdown();
            return;
        }

        if (this.isPrintableCharacter(e.key) || e.key === BACKSPACE_KEY || e.key === DELETE_KEY) {
            this.showDropdown();
        }

    }

    handleKeyDown = e => {

        if (e.key === ARROW_DOWN_KEY || e.key === ARROW_UP_KEY || e.key === ENTER_KEY || e.key === SPACE_KEY || e.key === ESCAPE_KEY) {

            if (!this.isActive && (e.key === ARROW_DOWN_KEY || e.key === ARROW_UP_KEY)) {
                this.showDropdown();
            }

            if (e.key === ARROW_DOWN_KEY) {
                e.preventDefault();
                e.stopPropagation();
                this.selectNextItem();
                return;
            }
            else if (e.key === ARROW_UP_KEY) {
                e.preventDefault();
                e.stopPropagation();
                this.selectPrevItem();
                return;
            }
            else if (this.isActive && e.key === ENTER_KEY || e.key === SPACE_KEY) {
                e.preventDefault();
                e.stopPropagation();
                this.hideDropdown();
                return;
            }
            else if (!this.isActive && e.key === ESCAPE_KEY) {
                this.element.value = "";
                return;
            }

        }

    }

    handleItemClick = e => {
        if (e.target.classList.contains(ITEM_CLASSNAME)) {
            this.selectItem(e.target);

            this.hideDropdown();
        }
    }

    query() {
        return this.options.query(this.element.value).then(result => {
            this.setDropdownContent(result);
            this._data = result;
        });
    }

    setDropdownContent(items) {
        this.dropdownElement.innerHTML = items.map(item => this.options.itemTemplate(item)).join("");
        this.allItems.forEach(item => {
            if (!item.hasAttribute("id")) {
                item.setAttribute("id", this.getUniqueId());
            }
        });
    }

    hideDropdown() {
        this.dropdown.hide();
        this.dropdownElement.innerHTML = "";
        this.element.removeAttribute("aria-activedescendant");
    }

    showDropdown() {

        this.query(this.element.value).then(data => {
            this.dropdown.show();
        });

    }

    selectNextItem() {

        let nextItem = null;

        if (!this.currentItem) {
            nextItem = this.firstItem;
        }
        else if (this.currentItem === this.lastItem) {
            nextItem = this.firstItem;
        }
        else {
            nextItem = this.currentItem.nextElementSibling;
        }

        this.selectItem(nextItem);

    }

    selectPrevItem() {

        let prevItem = null;

        if (!this.currentItem) {
            prevItem = this.lastItem;
        }
        else if (this.currentItem === this.firstItem) {
            prevItem = this.lastItem;
        }
        else {
            prevItem = this.currentItem.previousElementSibling;
        }

        this.selectItem(prevItem);

    }

    selectItem(item) {
        this.currentItem = item;

        this.element.value = this.currentItem.innerText;

        if (this.options.valueTarget) {
            this.options.valueTarget.value = this.currentItem.dataset.value;
        }

        this.element.setAttribute("aria-activedescendant", this.currentItem.id);

        const itemSelectedEvent = new CustomEvent("itemselected.bs.autocomplete", {
            detail: { data: this.getDataForItem(this.currentItem) }
        });

        this.element.dispatchEvent(itemSelectedEvent);
    }

    getDataForItem(item) {
        const index = Array.from(this.allItems).indexOf(item);
        return this._data[index];
    }

    getUniqueId() {
        return `bs-autocomplete-${++Autocomplete.uniqueItemIndex}`;
    }

    isPrintableCharacter(str) {
        return str.length === 1 && str.match(/\S| /);
    }

};